import { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, Clock, Users, TrendingUp, CalendarDays, AlertCircle, Pencil, Trash2, X, Save, ExternalLink, User } from 'lucide-react';
import IssueIdLink from '@/components/IssueIdLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  resolved: 'rgb(34, 197, 94)',
  open: 'rgb(245, 158, 11)',
  primary: 'rgb(59, 130, 246)',
  accent: 'rgb(139, 92, 246)',
  muted: 'rgb(113, 113, 122)'
};

function CalendarDashboard({ token }) {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [issuesStatus, setIssuesStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Edit state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', time: '', duration_hours: 1 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, statsRes, issuesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/calendar-dashboard/events`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/calendar-dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/calendar-dashboard/issues-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEvents(eventsRes.data.events || []);
      setStats(statsRes.data);
      setIssuesStatus(issuesRes.data);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event.id);
    setEditForm({
      date: event.event_date || '',
      time: event.event_time || '',
      duration_hours: event.duration_hours || 1
    });
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setEditForm({ date: '', time: '', duration_hours: 1 });
  };

  const handleSaveEdit = async (eventId) => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/calendar-events/${eventId}`,
        {
          event_date: editForm.date,
          event_time: editForm.time || null,
          duration_hours: editForm.duration_hours
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Evento aggiornato');
      setEditingEvent(null);
      fetchData();
    } catch (err) {
      console.error('Error updating event:', err);
      toast.error(err.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/calendar-events/${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Evento eliminato');
      fetchData();
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error(err.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPieData = () => {
    if (!issuesStatus) return [];
    return [
      { name: 'Risolte', value: issuesStatus.resolved, color: COLORS.resolved },
      { name: 'Aperte', value: issuesStatus.open, color: COLORS.open }
    ];
  };

  const getStatusBadge = (isResolved) => {
    if (isResolved) {
      return (
        <Badge className="bg-green-500/20 text-green-400 font-mono">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Risolta
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500/20 text-yellow-400 font-mono">
        <Clock className="h-3 w-3 mr-1" />
        Aperta
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
      </div>
    );
  }

  const today = stats?.today || new Date().toISOString().split('T')[0];
  const pastEvents = events.filter(e => e.event_date < today);
  const futureEvents = events.filter(e => e.event_date >= today);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-[rgb(250,250,250)]">
            Dashboard Calendarizzazioni
          </h1>
          <p className="text-[rgb(161,161,170)] font-mono mt-1">
            Analisi retrospettiva e prospettica degli eventi pianificati
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-8 w-8 text-[rgb(59,130,246)]" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[rgb(161,161,170)] font-mono text-sm">Totale Eventi</p>
                <p className="text-3xl font-bold font-mono text-[rgb(250,250,250)]">
                  {stats?.total_events || 0}
                </p>
              </div>
              <CalendarDays className="h-8 w-8 text-[rgb(59,130,246)]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[rgb(161,161,170)] font-mono text-sm">Eventi Passati</p>
                <p className="text-3xl font-bold font-mono text-[rgb(250,250,250)]">
                  {stats?.past_events || 0}
                </p>
              </div>
              <Clock className="h-8 w-8 text-[rgb(161,161,170)]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[rgb(161,161,170)] font-mono text-sm">Eventi Futuri</p>
                <p className="text-3xl font-bold font-mono text-[rgb(250,250,250)]">
                  {stats?.future_events || 0}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[rgb(161,161,170)] font-mono text-sm">Issue Risolte</p>
                <p className="text-3xl font-bold font-mono text-green-400">
                  {issuesStatus?.resolved || 0}
                  <span className="text-sm text-[rgb(161,161,170)]">
                    /{issuesStatus?.total || 0}
                  </span>
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)]">
          <TabsTrigger value="overview" className="font-mono data-[state=active]:bg-[rgb(59,130,246)]">
            Panoramica
          </TabsTrigger>
          <TabsTrigger value="retrospective" className="font-mono data-[state=active]:bg-[rgb(59,130,246)]">
            Retrospettiva
          </TabsTrigger>
          <TabsTrigger value="prospective" className="font-mono data-[state=active]:bg-[rgb(59,130,246)]">
            Prospettica
          </TabsTrigger>
          <TabsTrigger value="all-events" className="font-mono data-[state=active]:bg-[rgb(59,130,246)]">
            Tutti gli Eventi
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Issue Status Pie Chart */}
            <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
              <CardHeader>
                <CardTitle className="font-mono text-[rgb(250,250,250)]">
                  Stato Issue Calendarizzate
                </CardTitle>
              </CardHeader>
              <CardContent>
                {issuesStatus && issuesStatus.total > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={getPieData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {getPieData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgb(24,24,27)', 
                          border: '1px solid rgb(39,39,42)',
                          borderRadius: '8px',
                          fontFamily: 'monospace'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-[rgb(161,161,170)] font-mono">
                    <div className="text-center">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nessun evento calendarizzato</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Trend */}
            <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
              <CardHeader>
                <CardTitle className="font-mono text-[rgb(250,250,250)]">
                  Trend Mensile
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.events_by_month && stats.events_by_month.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.events_by_month}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgb(39,39,42)" />
                      <XAxis 
                        dataKey="month" 
                        stroke="rgb(161,161,170)"
                        tick={{ fontFamily: 'monospace', fontSize: 12 }}
                      />
                      <YAxis 
                        stroke="rgb(161,161,170)"
                        tick={{ fontFamily: 'monospace', fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{ 
                          backgroundColor: 'rgb(24,24,27)', 
                          border: '1px solid rgb(39,39,42)',
                          borderRadius: '8px',
                          fontFamily: 'monospace'
                        }}
                      />
                      <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-[rgb(161,161,170)] font-mono">
                    <div className="text-center">
                      <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nessun dato disponibile</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Retrospective Tab - Past Events & Issue Resolution */}
        <TabsContent value="retrospective" className="mt-6">
          <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
            <CardHeader>
              <CardTitle className="font-mono text-[rgb(250,250,250)] flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Analisi Retrospettiva - Issue Calendarizzate
              </CardTitle>
              <p className="text-sm text-[rgb(161,161,170)] font-mono">
                Verifica lo stato di risoluzione delle issue che sono state pianificate
              </p>
            </CardHeader>
            <CardContent>
              {issuesStatus && issuesStatus.issues.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[rgb(39,39,42)]">
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">ID</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Summary</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Progetto</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Assegnato</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Stato</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Risoluzione</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {issuesStatus.issues.map((issue) => (
                        <TableRow key={issue.id} className="border-[rgb(39,39,42)] hover:bg-white/5">
                          <TableCell className="font-mono text-sm">
                            <IssueIdLink issueId={issue.id} />
                          </TableCell>
                          <TableCell className="max-w-md truncate text-sm">{issue.summary}</TableCell>
                          <TableCell className="text-sm">{issue.project}</TableCell>
                          <TableCell className="text-sm">{issue.handler}</TableCell>
                          <TableCell>
                            <Badge 
                              className="font-mono text-xs"
                              style={{ 
                                backgroundColor: issue.is_resolved ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.2)',
                                color: issue.is_resolved ? 'rgb(34,197,94)' : 'rgb(245,158,11)'
                              }}
                            >
                              {issue.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(issue.is_resolved)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-[rgb(161,161,170)] font-mono">
                  <div className="text-center">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessuna issue calendarizzata trovata</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Prospective Tab - Future Events by User */}
        <TabsContent value="prospective" className="mt-6">
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <Calendar className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-[rgb(161,161,170)] font-mono text-sm">Eventi Futuri</p>
                      <p className="text-2xl font-bold font-mono text-[rgb(250,250,250)]">
                        {futureEvents.length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <Users className="h-6 w-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[rgb(161,161,170)] font-mono text-sm">Utenti Coinvolti</p>
                      <p className="text-2xl font-bold font-mono text-[rgb(250,250,250)]">
                        {Object.keys(stats?.events_by_user || {}).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-500/20 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-[rgb(161,161,170)] font-mono text-sm">Media/Utente</p>
                      <p className="text-2xl font-bold font-mono text-[rgb(250,250,250)]">
                        {Object.keys(stats?.events_by_user || {}).length > 0 
                          ? (stats.total_events / Object.keys(stats.events_by_user).length).toFixed(1)
                          : 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Future Events Table */}
            <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
              <CardHeader>
                <CardTitle className="font-mono text-[rgb(250,250,250)] flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Eventi Pianificati (Futuri)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {futureEvents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[rgb(39,39,42)]">
                          <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Data</TableHead>
                          <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Ora</TableHead>
                          <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Issue</TableHead>
                          <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Titolo</TableHead>
                          <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Durata</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {futureEvents.map((event) => (
                          <TableRow key={event.id} className="border-[rgb(39,39,42)] hover:bg-white/5">
                            <TableCell className="font-mono text-sm">
                              {formatDate(event.event_date)}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {event.is_all_day ? (
                                <Badge className="bg-blue-500/20 text-blue-400 font-mono text-xs">
                                  Tutto il giorno
                                </Badge>
                              ) : (
                                event.event_time || 'N/A'
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              <IssueIdLink issueId={event.issue_id} />
                            </TableCell>
                            <TableCell className="max-w-md truncate text-sm">
                              {event.issue_summary?.replace(/^\[#\d+\]\s*/, '') || 'N/A'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {event.duration_hours}h
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 text-[rgb(161,161,170)] font-mono">
                    <div className="text-center">
                      <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nessun evento futuro pianificato</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* All Events Tab */}
        <TabsContent value="all-events" className="mt-6">
          <Card className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
            <CardHeader>
              <CardTitle className="font-mono text-[rgb(250,250,250)] flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Tutti gli Eventi Calendarizzati
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[rgb(39,39,42)]">
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Data Evento</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Ora</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Issue</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Titolo</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Utente</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Periodo</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)] w-32">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event) => {
                        const isPast = event.event_date < today;
                        const isEditing = editingEvent === event.id;
                        
                        return (
                          <TableRow key={event.id} className="border-[rgb(39,39,42)] hover:bg-white/5">
                            <TableCell className="font-mono text-sm">
                              {isEditing ? (
                                <Input
                                  type="date"
                                  value={editForm.date}
                                  onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                                  className="w-36 bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-white font-mono text-xs"
                                />
                              ) : (
                                formatDate(event.event_date)
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {isEditing ? (
                                <Input
                                  type="time"
                                  value={editForm.time}
                                  onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                                  className="w-24 bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-white font-mono text-xs"
                                />
                              ) : event.is_all_day ? (
                                <span className="text-[rgb(161,161,170)]">Tutto il giorno</span>
                              ) : (
                                event.event_time || 'N/A'
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              <IssueIdLink issueId={event.issue_id} />
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-sm">
                              {event.issue_summary?.replace(/^\[#\d+\]\s*/, '') || 'N/A'}
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="flex items-center gap-1.5">
                                <User className="h-3 w-3 text-[rgb(161,161,170)]" />
                                <span className="font-mono text-[rgb(250,250,250)]">
                                  {event.user_name || 'N/A'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className={`font-mono text-xs ${
                                  isPast 
                                    ? 'bg-[rgb(39,39,42)] text-[rgb(161,161,170)]' 
                                    : 'bg-green-500/20 text-green-400'
                                }`}
                              >
                                {isPast ? 'Passato' : 'Futuro'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {isEditing ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleSaveEdit(event.id)}
                                      disabled={saving}
                                      className="h-7 w-7 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                    >
                                      <Save className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={handleCancelEdit}
                                      className="h-7 w-7 p-0 text-[rgb(161,161,170)] hover:text-white hover:bg-white/10"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    {event.google_event_link && (
                                      <a
                                        href={event.google_event_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-7 w-7 p-0 inline-flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(59,130,246)]"
                                        title="Apri in Google Calendar"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                      </a>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEdit(event)}
                                      className="h-7 w-7 p-0 text-[rgb(161,161,170)] hover:text-[rgb(59,130,246)] hover:bg-blue-500/10"
                                      title="Modifica"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDelete(event.id)}
                                      className="h-7 w-7 p-0 text-[rgb(161,161,170)] hover:text-red-400 hover:bg-red-500/10"
                                      title="Elimina"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-[rgb(161,161,170)] font-mono">
                  <div className="text-center">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun evento calendarizzato</p>
                    <p className="text-sm mt-2">Aggiungi issue al calendario dalle viste Kanban, Progetti, Utenti o Categorie</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default CalendarDashboard;
